import moment from "moment";

import AuditLogListView from "issara-sdk/apis/AuditLogListView_core";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = { DEFAULT: "DEFAULT", SEARCH: "SEARCH" } as const;

export const SEQUENCE_NAME = "EmployeeHistory" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  EmployeeHistorySequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    items: AuditLogType[];
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "selectedEmployee">;

export type MasterOptions = MasterMapOptions<typeof Masters>;

type AuditLogType = Record<string, any>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  EmployeeHistorySequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["division", {}],
  ["positionAll", {}],
] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  await controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    { EmployeeHistorySequence: { ...state.EmployeeHistorySequence, sequenceIndex: "Action" } },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const [[employee], [salary]] = await Promise.all([
    AuditLogListView.list({
      apiToken: controller.apiToken,
      params: { ids: state.selectedEmployee?.id, limit: 99999, model: "employee" },
    }),
    AuditLogListView.list({
      apiToken: controller.apiToken,
      params: {
        filter_keys: "employee_id",
        filter_vals: state.selectedEmployee?.id,
        limit: 99999,
        model: "salary",
      },
    }),
  ]);

  const employeeItems: Record<string, any>[] = employee?.items || [];
  const salaryItems: Record<string, any>[] = salary?.items || [];

  const formattedEmployee = employeeItems
    .map((item) => ({
      action: item.action,
      affiliate: item.after.affiliate,
      after: item.after,
      created: item.created,
      diff: item.diff,
      edit_user: item.edit_user,
      employee: item.after.id,
      position: item.after.position,
    }))
    .filter((item) => item.employee === state.selectedEmployee?.id);

  const formattedSalary = salaryItems
    .map((item) => ({
      action: item.action,
      after: item.after,
      amount: item.after.amount,
      base_ot: item.after.base_ot,
      created: item.created,
      diff: item.diff,
      edit_user: item.edit_user,
      employee: item.after.employee,
    }))
    .filter((item) => item.employee === state.selectedEmployee?.id);

  const items = mergeByClosestDate(formattedEmployee, formattedSalary);

  controller.setState({
    EmployeeHistorySequence: {
      ...state.EmployeeHistorySequence,
      items,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: null },
  });
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */
const mergeByClosestDate = (employees: Record<string, any>[], salaries: Record<string, any>[]) => {
  const result: Record<string, any>[] = [];

  // หา Record ที่ created ก่อนและใกล้เคียงที่สุด
  const findPrevious = (source: Record<string, any>, targets: Record<string, any>[]) => {
    const sourceDate = moment(source.created); // เวลาที่ log

    let match = null;
    let minDiff = Infinity;

    for (const target of targets) {
      const targetCreated = moment(target.created);
      const recordCreated = moment(target.after.created);

      // เช็คว่า target.created ไม่เกิน source.created
      if (targetCreated.isAfter(sourceDate)) {
        continue; // skip target นี้
      }

      const diff = sourceDate.diff(recordCreated); // log time - record creation time

      if (diff > 0 && diff < minDiff) {
        minDiff = diff;
        match = target;
      }
    }

    return match;
  };

  // สร้าง change tracking จาก diff
  const addChangeFields = (record: Record<string, any>, fields: string[]) => {
    const changes: Record<string, any> = {};
    const diff = record.diff?.values_changed || {};

    for (const field of fields) {
      if (diff[`root['${field}']`] || record.action === "CREATE") {
        changes[`${field}_changed`] = true;
      }
    }

    return changes;
  };

  // สร้างผลลัพธ์
  const formatRecord = (
    emp: Record<string, any> | null,
    sal: Record<string, any> | null,
    changes: string[]
  ) => {
    const record: Record<string, any> = {
      affiliate: emp?.affiliate || null,
      amount: sal?.amount || null,
      base_ot: sal?.base_ot || null,
      created: emp?.created || sal?.created,
      edit_user: emp?.edit_user || sal?.edit_user,
      position: emp?.position || null,
    };

    // เพิ่ม change tracking สำหรับ employee fields
    if (emp && changes.includes("employee")) {
      const empChanges = addChangeFields(emp, ["affiliate", "position"]);

      Object.assign(record, empChanges);
    }

    // เพิ่ม change tracking สำหรับ salary fields
    if (sal && changes.includes("salary")) {
      const salChanges = addChangeFields(sal, ["amount", "base_ot"]);

      Object.assign(record, salChanges);
    }

    return record;
  };

  // Employee หา Salary ที่ created ก่อน
  for (const emp of employees) {
    const prevSal = findPrevious(emp, salaries);
    const formatted = formatRecord(emp, prevSal, ["employee"]);

    result.push({ ...formatted, created: emp.created });
  }

  // Salary หา Employee ที่ created ก่อน
  for (const sal of salaries) {
    const prevEmp = findPrevious(sal, employees);
    const formatted = formatRecord(prevEmp, sal, ["salary"]);

    result.push({ ...formatted, created: sal.created });
  }

  // เรียงตาม created date
  return result.sort((a, b) => moment(b.created).diff(moment(a.created)));
};
